import { useTranslation } from "react-i18next";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import ScheduledReportsEditorUtils, { ReportScheduleFormType, ScheduledReportsAdminMode, ScheduledReportsFormType, exportOptionRadioButtons, reportTypeRadioButtons } from "../ScheduledReportsEditorUtils";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useReportConfigurationData } from "../../../queries/report/UseReportConfigurationData";
import { Alert, List, MenuItem, Stack, Typography } from "@mui/material";
import { ExportFormatTypeEnum, ReportTypeEnum } from "../../../services/reporting-data-service/ReportScheduleModels";
import ScheduleCard from "./ScheduleCard";
import { useGetGroupMarketingReportTats } from "../../../queries/group-marketing-report/UseGetGroupMarketingReportTats";
import { Scope } from "../../../common/enums/DRCXEnums";
import _ from "lodash";
import { ContentContainer } from "../../content-container/ContentContainer";
import { ContentSection } from "../../content-section/ContentSection";
import FormInput from "../../forms/form-input/FormInput";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import FormProperty from "../../forms/form-property/FormProperty";
import FormRadioGroup from "../../forms/form-radio-group/FormRadioGroup";
import FormSelect from "../../forms/form-select/FormSelect";
import LoadingZone from "../../loading-zone/LoadingZone";
import StepMenuLandingCardList from "../../step-menu/StepMenuLandingCardList";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { ScheduledReportsRecipientListItem } from "../scheduled-reports-notifications/scheduled-reports-recipients/ScheduledReportsRecipientListItem";

export type ScheduledReportsPropertiesProps = {
    groupID: number,
    myReports: boolean
};

export default function ScheduledReportsProperties({
    groupID,
    myReports
}: ScheduledReportsPropertiesProps) {

    const { t } = useTranslation();
    const [userState] = useUserContext();

    const {
        //getValues,
        watch,
        //trigger,
        control,
        //reset,
        setValue,
        //resetField
    } = useFormContext<ScheduledReportsFormType>();    

    const reportTaskForm = useWatch({ control: control }) as ScheduledReportsFormType;

    const [
        exportFormat,
        reportType,
        watchrecipients
    ] = watch([
        'exportFormat',
        'reportType',
        'recipients'
    ]);

    const {
        fields: recipients,
    } = useFieldArray({
        control: control,
        name: 'recipients',
        keyName: "fieldKey"
    });    

    const {
        data: reportList, status
    } = useQueryNotifications(useReportConfigurationData)({
        groupID: groupID,
        includeChildGroups: !myReports && reportTaskForm.id < 0,
        includeInaccessibleReports: true,
        enabled: reportType !== ReportTypeEnum.GroupMarketing
    });

    const {
        data: tats, status: tatStatus
    } = useQueryNotifications(useGetGroupMarketingReportTats)({
        groupID: groupID,
        enabled: reportType === ReportTypeEnum.GroupMarketing
    });
    

    reportList?.configurations?.forEach(o => {
        if (o.scope === Scope.Custom && (!o.customCode || o.customCode.length === 0)) {
            o.scope = Scope.User;
        }
    });

    const reports = reportList?.configurations ? 
                    reportType === ReportTypeEnum.User ?
                    reportList.configurations.filter(o => o.scope === Scope.User) :
                    reportType === ReportTypeEnum.Custom ?
                    reportList.configurations.filter(o => o.scope === Scope.Custom) :
                    [] : [];

    const isLoading = status === 'loading' || tatStatus === 'loading';

    const mode = (reportTaskForm.id ?? 0) <= 0 ? ScheduledReportsAdminMode.Add : ScheduledReportsAdminMode.Edit;

    function onReportTypeChange() {

        setValue('reportConfigID', -1, {shouldDirty: true});
        setValue('overrideTatID', -1, {shouldDirty: true});
    }

    const {
        fields: schedules,
        append,
        remove,
        update
    } = useFieldArray({
        control: control,
        name: 'schedules',
        keyName: "fieldKey"
    });

    function getNextID() {
        const ids = schedules.filter(o => o.id < 0).map(o => o.id) ?? [];
        let nextID = ids.length === 0 ? -1 : Math.min(...ids) - 1;

        if (nextID > 0) {
            nextID = -1;
        }

        return nextID;
    }

    function onAddSchedule() {

        const nextID = getNextID();

        const newSchedule = ScheduledReportsEditorUtils.getNewFormSchedule(nextID, 
                                userState.user?.timeZoneIndex as number,
                                schedules);

        append(newSchedule, { shouldFocus: false });
    }

    function onDuplicateSchedule(item: ReportScheduleFormType) {

        const nextID = getNextID();
        const newSchedule = _.cloneDeep(item);
        newSchedule.id = getNextID();
        newSchedule.description += Math.abs(nextID) > 1 ? 
                        ` (Copy ${Math.abs(nextID)})` : 
                        " (Copy)";
        append(newSchedule, { shouldFocus: false });
    }

    function onDeleteSchedule(item: ReportScheduleFormType) {
        const index = schedules.findIndex(o => o.id === item.id);
        remove(index)
    }

    function _expandSchedules(value: boolean) {
        schedules.forEach((o, index) => {
            update(index, {
                ...o,
                expanded: value
            });
        });
    }

    function onExpandAllSections() {
        _expandSchedules(true);
    }

    function onCollapseAllSections() {
    _expandSchedules(false);
    }

    const _validateDescription = (value: string): boolean => {
        return value.toString().trim().length <= 512;
    }

   return (
       <LoadingZone isLoading={isLoading}>
           {!isLoading && <ContentContainer>
               <ContentSection sx={{
                   padding: 1,
                   overflowY: 'auto'
               }}>
               
               <FormPropertySection label={t('properties.standard')}>

                       <FormProperty>

                           <FormSelect
                               name="reportType"
                               label={t('scheduledReports.reportType')}
                               readOnly={mode === ScheduledReportsAdminMode.Edit}
                               sx={{ 
                                   width: 1,
                                   marginTop: 1,
                               }}
                               onChange={() => onReportTypeChange()}
                           >
                               {
                                   reportTypeRadioButtons.map((o) => 
                                       <MenuItem key={o.value} value={o.value}>
                                           {o.label}
                                       </MenuItem>
                                   )
                               }
                           </FormSelect>

                       </FormProperty>

                       <FormProperty>


                       {reportType !== ReportTypeEnum.GroupMarketing && 
                           <FormSelect
                               name="reportConfigID"
                               label={t('scheduledReports.report')}
                               readOnly={mode === ScheduledReportsAdminMode.Edit}
                               sx={{ 
                                   width: 1,
                                   marginTop: 1,
                               }}
                           >
                               <MenuItem value={-1} disabled={true}>
                                   {t('scheduledReports.selectReport')}
                               </MenuItem>

                               {
                                   reports.map((o, ndx) => 
                                       <MenuItem key={ndx} value={o.id}>
                                           {o.name}
                                       </MenuItem>
                                   )
                               }
                           </FormSelect>
                       }


                       {reportType === ReportTypeEnum.GroupMarketing && 
                           <FormSelect
                               name="overrideTatID"
                               label={t('scheduledReports.steps.details.reportTag')}
                               readOnly={mode === ScheduledReportsAdminMode.Edit}
                               sx={{ 
                                   width: 1,
                                   marginTop: 1,
                               }}
                           >
                               <MenuItem value={-1} disabled={true}>
                                   {t('scheduledReports.steps.details.selectGroupMarketingTat')}
                               </MenuItem>

                               {
                                   tats?.map((o, ndx) => 
                                       <MenuItem key={ndx} value={o.id}>
                                           {o.name}
                                       </MenuItem>
                                   )
                               }
                           </FormSelect>
                           }


                       </FormProperty>

                   </FormPropertySection>

                   <FormPropertySection label=''>
                       <FormProperty>
                           <FormInput
                               name="description"
                               label={t('scheduledReports.reportDescription')}
                               sx={{
                                   width: 1,
                                   marginTop: 1,
                                   marginBottom: 1,
                               }}
                               rows={4}
                               multiline={true}
                               rules={{
                                   validate: (value: string) => !_validateDescription(value) ?
                                   t('scheduledReports.descriptionLength') : 
                                   true
                               }}

                           />
                       </FormProperty>

                       <FormProperty>
                           <FormRadioGroup
                               name='exportFormat'
                               label={t('scheduledReports.exportFormat')}       
                               direction='row'
                               radioButtons={reportType === ReportTypeEnum.GroupMarketing ?
                                   exportOptionRadioButtons.filter(o => o.value === ExportFormatTypeEnum.Excel || 
                                       o.value === ExportFormatTypeEnum.Pdf) :
                                   exportFormat !== ExportFormatTypeEnum.Other ?
                                   exportOptionRadioButtons.filter(o => o.value !== ExportFormatTypeEnum.Other) :
                                   exportOptionRadioButtons
                               } 

                               readOnly={exportFormat === ExportFormatTypeEnum.Other}
                           />

                       </FormProperty>

                   </FormPropertySection>

                   {
                   myReports && 
                   <Stack direction='column' 
                       sx={{
                       marginBottom: 1,
                       flex: '1 1 auto'
                   }}>

                       <Typography variant="subtitle1" sx={{
                           fontWeight: 600
                       }} >
                           {getObjectTypeName(eLynxObjectTypeEnum.Recipient, true)}
                       </Typography>

                       <List sx={{
                           paddingTop: 0,
                           overflow: "hidden",
                           display: 'flex',
                           flexDirection: 'column',
                           flex: '1 1 auto'
                       }}>
                           {recipients.map((o) => (

                               <ScheduledReportsRecipientListItem
                                   item={o}
                                   key={o.fieldKey}
                               />
                           ))}
                       </List>

                       {
                       !watchrecipients.some(o => o.selected) && 
                       <Alert severity="warning" sx={{marginBottom: 1}}>
                           {t('scheduledReports.steps.details.recipientRequired')}
                       </Alert>
                       }
                   </Stack>

                   }

                   <Stack direction='column' 
                       sx={{
                           backgroundColor: "custom.background.cardContainer",
                           flex: '1 1 auto'
                   }}>

                       <StepMenuLandingCardList
                           onExpandAll={onExpandAllSections}
                           onCollapseAll={onCollapseAllSections}
                           onAddSection={onAddSchedule}
                           addSectionButtonLabel={t("scheduledReports.steps.details.addSchedule")}
                           title={t("scheduledReports.schedulesLabel")}
                           disableAddSectionButton={false}
                       >
                           {
                           schedules.map((o, index) => 

                           (
                               <ScheduleCard key={index}
                                   item={o}
                                       duplicateEnabled={true}
                                       deleteEnabled={schedules.length > 1}
                                       onDuplicate={onDuplicateSchedule}
                                       onDelete={onDeleteSchedule}
                                   />
                          
                           ))
                           }
                       </StepMenuLandingCardList>

                       </Stack>

               </ContentSection>        
           </ContentContainer>
           }
       </LoadingZone>
   );
  }
